/**
 * Generators API Service
 * Handles all generator-related API calls
 */

import apiClient from '../client';
import type {
  GeneratorMapPoint,
  GeneratorMapParams,
  ClusterPoint,
  ClusterParams,
} from '../types/generators.types';

const GENERATORS_BASE_URL = '/generators';

/**
 * Generators Service
 * All methods for interacting with the generators API
 */
export const generatorsService = {
  /**
   * Get all generators with GPS coordinates for map display
   * GET /v2/api/generators/map
   *
   * @param params - Query parameters (status filter)
   * @returns Array of generators with GPS coordinates
   */
  getGeneratorsMap: async (
    params?: GeneratorMapParams
  ): Promise<GeneratorMapPoint[]> => {
    const response = await apiClient.get<GeneratorMapPoint[]>(
      `${GENERATORS_BASE_URL}/map`,
      { params }
    );
    return response.data;
  },

  /**
   * Get generator clusters for map display (for large datasets)
   * GET /v2/api/generators/map/clusters
   *
   * @param params - Query parameters (zoom, status, bounds)
   * @returns Array of cluster points
   */
  getGeneratorsClusters: async (
    params: ClusterParams
  ): Promise<ClusterPoint[]> => {
    const response = await apiClient.get<ClusterPoint[]>(
      `${GENERATORS_BASE_URL}/map/clusters`,
      { params }
    );
    return response.data;
  },

  /**
   * Search generators
   * GET /v2/api/generators
   *
   * @param query - Search query string
   * @param params - Additional query parameters (status)
   * @returns Array of matching generators
   */
  searchGenerators: async (
    query: string,
    params?: GeneratorMapParams
  ): Promise<GeneratorMapPoint[]> => {
    const response = await apiClient.get<GeneratorMapPoint[]>(
      GENERATORS_BASE_URL,
      { params: { search: query, ...params } }
    );
    return response.data;
  },
};
